import {
  membershipToolDef,
  type MembershipToolDef,
} from '@form-factory/schema/contracts';
import {
  MembershipResponseType,
  type UserGender,
} from '@form-factory/schema/enums';
import { useLingui } from '@lingui/react/macro';

// Import the clean sub-components from the membershipTool directory
import { ToolNoDataError } from './components/ToolNoDataError';
import { MembershipLogin } from './membershipTool/components/MembershipLogin';
import { MembershipRegistration } from './membershipTool/components/MembershipRegistration';
import { MembershipStatus } from './membershipTool/components/MembershipStatus';
import { UserProfile } from './membershipTool/components/UserProfile';
import { getToolContent, isToolContentKey } from './utils/contentTranslation';
import { createToolComponent } from '../../toolRegistry';
import { Markdown } from '../Markdown';
import { Message } from '../ui';

// V1 format type definitions for backward compatibility
type V1MembershipResponse = {
  type:
    | 'membership_login'
    | 'membership_registration'
    | 'membership_status'
    | 'user_profile'
    | 'other';
  content?: string;
  // membership_login fields
  shouldLogin?: boolean;
  // membership_registration fields
  shouldRegister?: boolean;
  // membership_status fields
  activeMembership?: string;
  activeFrom?: string;
  activeTo?: string;
  isExpired?: boolean;
  showNewMembershipButton?: boolean;
  showPauseMembershipButton?: boolean;
  id?: string; // legacy field
  canBePaused?: boolean; // legacy field
  // user_profile fields
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  gender?: UserGender;
  birthDate?: string;
  address?: string;
  isEmailVerified?: boolean;
};

/**
 * Normalizes both V1 and V2 response formats to a consistent V2 structure
 * V1 format: { type: 'membership_login', shouldLogin: true, content: '...' }
 * V2 format: { type: 'membership_login', membership_login: { shouldLogin: true }, content: '...' }
 *
 * This ensures backward compatibility with older tool implementations that
 * may still return V1 format responses.
 */
function normalizeResponse(
  args: V1MembershipResponse | MembershipToolDef | any,
): MembershipToolDef {
  // Safety check for invalid input
  if (!args || typeof args !== 'object' || !args.type) {
    return args as MembershipToolDef;
  }

  // If it already has the nested structure (V2), return as-is
  if (args.type && args[args.type]) {
    return args as MembershipToolDef;
  }

  // Convert V1 format to V2 format
  const normalized: any = {
    type: args.type,
    content: args.content,
  };

  // Map V1 fields to V2 nested structure based on type
  switch (args.type) {
    case 'membership_login':
      normalized.membership_login = {
        shouldLogin: args.shouldLogin ?? true,
      };
      break;

    case 'membership_registration':
      normalized.membership_registration = {
        shouldRegister: args.shouldRegister ?? true,
      };
      break;

    case 'membership_status':
      normalized.membership_status = {
        activeMembership: args.activeMembership,
        activeFrom: args.activeFrom,
        activeTo: args.activeTo,
        isExpired: args.isExpired ?? false,
        showNewMembershipButton: args.showNewMembershipButton ?? false,
        showPauseMembershipButton: args.showPauseMembershipButton ?? false,
        // Note: id and canBePaused are legacy V1 fields not in the current V2 schema
        // but we preserve them for backward compatibility if they exist
      };
      // Only add legacy fields if they exist in V1 response
      if (args.id !== undefined) {
        (normalized.membership_status as any).id = args.id;
      }
      if (args.canBePaused !== undefined) {
        (normalized.membership_status as any).canBePaused = args.canBePaused;
      }
      break;

    case 'user_profile':
      normalized.user_profile = {
        firstName: args.firstName ?? '',
        lastName: args.lastName ?? '',
        email: args.email ?? '',
        phone: args.phone,
        gender: args.gender,
        birthDate: args.birthDate,
        address: args.address,
        isEmailVerified: args.isEmailVerified ?? false,
      };
      break;

    case 'other':
      // 'other' type doesn't have nested structure in V2
      break;

    default:
      // Unknown type, return as-is
      return args;
  }

  return normalized as MembershipToolDef;
}

export const MembershipTool = createToolComponent(
  {
    schema: {
      content: membershipToolDef,
    },
  },
  ({ toolCall, state }) => {
    const { i18n } = useLingui();

    // Don't render during streaming
    if (state === 'call' || state === 'call-partial') {
      return null;
    }

    // Get data - ToolProvider already unwrapped it
    const rawData =
      state === 'result' && toolCall.content ? toolCall.content : toolCall.args;

    // Validate we have required fields
    if (
      !rawData ||
      typeof rawData !== 'object' ||
      !('type' in rawData) ||
      !rawData.type
    ) {
      if (state === 'result') {
        return <ToolNoDataError />;
      }

      return null;
    }

    // Normalize the response to handle both V1 and V2 formats
    const args = normalizeResponse(rawData);

    if (!args.content && state === 'result') {
      // If we have structured content, render components even without content text
      if (
        args.type === MembershipResponseType.MembershipStatus &&
        args.membership_status
      ) {
        return (
          <Message.BubbleGroup>
            <MembershipStatus
              status={{ ...args.membership_status, content: '' }}
            />
          </Message.BubbleGroup>
        );
      }

      if (
        args.type === MembershipResponseType.UserProfile &&
        args.user_profile
      ) {
        return (
          <Message.BubbleGroup>
            <UserProfile profile={{ ...args.user_profile, content: '' }} />
          </Message.BubbleGroup>
        );
      }

      return <ToolNoDataError />;
    }

    return (
      <Message.BubbleGroup>
        {args.type === MembershipResponseType.MembershipStatus &&
          args.membership_status && (
            <MembershipStatus
              status={{
                ...args.membership_status,
                // Suppress loading placeholder content when UI is rendered
                content:
                  args.content === 'tool.membership.status.loading' ||
                  (isToolContentKey(args.content || '') &&
                    (args.content as string) ===
                      'tool.membership.status.loading')
                    ? ''
                    : getToolContent(args.content || '', args.type, i18n),
              }}
            />
          )}

        {args.type === MembershipResponseType.UserProfile &&
          args.user_profile && (
            <UserProfile
              profile={{
                ...args.user_profile,
                content:
                  args.content === 'tool.membership.profile.loading' ||
                  (isToolContentKey(args.content || '') &&
                    (args.content as string) ===
                      'tool.membership.profile.loading')
                    ? ''
                    : getToolContent(args.content || '', args.type, i18n),
              }}
            />
          )}

        {args.type === MembershipResponseType.MembershipRegistration &&
          args.membership_registration && (
            <MembershipRegistration
              registration={{
                ...args.membership_registration,
                content: getToolContent(args.content || '', args.type, i18n),
              }}
            />
          )}

        {args.type === MembershipResponseType.MembershipLogin &&
          args.membership_login && (
            <MembershipLogin
              login={{
                ...args.membership_login,
                content: getToolContent(args.content || '', args.type, i18n),
              }}
            />
          )}

        {args.type === MembershipResponseType.Other && (
          <Message.Bubble>
            <Markdown>
              {getToolContent(args.content || '', args.type, i18n)}
            </Markdown>
          </Message.Bubble>
        )}
      </Message.BubbleGroup>
    );
  },
);
